/**
 * MfaVerifyModal — the MFA challenge (verify + enroll) rendered as a MODAL
 * on top of the login screen, for BOTH hosts (admin SA + tenant user).
 *
 * P0 passkey-v2 loop fix
 * ──────────────────────
 * The previous implementation hosted the verify + enroll flows on their own
 * ROUTES (``/…/login/mfa-verify`` etc.). Each route had a
 * ``useEffect(() => { if (!mfaPendingToken) navigate("…/login") })`` guard.
 * On a successful verify, ``loginSuccess`` cleared ``mfaPendingToken`` — which
 * tripped that navigate-on-null-token effect and RACED the onComplete navigate
 * to the app, sometimes bouncing the user back to /login (the login loop).
 *
 * CRITICAL INVARIANT (do not regress): this modal derives its visibility from
 * ``mfaPendingToken`` alone. When the token clears (verify success / expiry),
 * the Login page simply stops rendering the modal. There is NO
 * navigate-on-null-token effect anywhere — that effect WAS the loop. The only
 * navigation is the explicit ``onComplete`` (to the app) fired by the flow
 * components after the session is committed, and ``onClose`` (back to the login
 * form, no route change) fired on expiry / dismiss.
 *
 * The flow components (``PasskeyVerifyFlow``, ``MfaVerifyFlow``,
 * ``MfaEmailVerifyFlow``, ``MfaEnrollFlow``) are UNCHANGED and hosted here as
 * children. They already commit the session via ``useAuthStore.loginSuccess``
 * on their internal success paths, then invoke the ``onComplete`` prop.
 */
import { useEffect, useState } from "react";
import { MfaVerifyFlow } from "@/components/auth/MfaVerifyFlow";
import { MfaEmailVerifyFlow } from "@/components/auth/MfaEmailVerifyFlow";
import { MfaEnrollFlow } from "@/components/auth/MfaEnrollFlow";
import { PasskeyVerifyFlow } from "@/components/auth/PasskeyVerifyFlow";
import { useAdminMfaVerify } from "@/hooks/queries/auth/useMfaVerify";
import {
  useTenantMfaVerify,
  useTenantMfaEmailVerify,
  useTenantMfaEmailResend,
} from "@/hooks/queries/auth/useMfaVerify";
import {
  useAdminMfaEnroll,
} from "@/hooks/queries/auth/useMfaEnroll";
import {
  useAdminMfaEnrollConfirm,
} from "@/hooks/queries/auth/useMfaEnrollConfirm";
import {
  useTenantMfaEnroll,
} from "@/hooks/queries/auth/useMfaEnroll";
import {
  useTenantMfaEnrollConfirm,
} from "@/hooks/queries/auth/useMfaEnrollConfirm";
import {
  useWebauthnVerifyBegin,
  useWebauthnVerifyFinish,
} from "@/hooks/queries/admin/useAdminWebauthn";
import {
  useTenantWebauthnVerifyBegin,
  useTenantWebauthnVerifyFinish,
} from "@/hooks/queries/auth/useTenantWebauthn";
import { useAuthStore } from "@/stores/useAuthStore";

/** Whether @simplewebauthn can run in this browser. */
function webAuthnSupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

interface ShellProps {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}

/**
 * The visual chrome — a centered dialog panel over a blurred backdrop. Matches
 * the ``components/common/Modal`` idioms (surface-overlay + backdrop-blur, an
 * elevated panel with a close affordance). It is NOT the store-driven
 * confirm ``Modal`` (that one hard-codes Cancel/Confirm buttons); the MFA
 * challenge hosts arbitrary flow children instead.
 */
function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  wide = false,
}: ShellProps): JSX.Element {
  const max = wide ? "max-w-lg" : "max-w-sm";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay p-4 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      aria-labelledby="mfa-modal-title"
      data-testid="mfa-verify-modal"
    >
      <div
        className={`w-full ${max} rounded-2xl border border-border-default bg-surface-elevated p-6 shadow-lg`}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2
              id="mfa-modal-title"
              className="text-lg font-semibold text-text-emphasis"
            >
              {title}
            </h2>
            <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/70">
              {subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-accent-primary hover:bg-surface-canvas dark:text-brand-100/50 dark:hover:bg-white/[0.04]"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Admin (SA host) variant ───────────────────────────────────────────────────

interface VariantProps {
  onComplete: () => void;
  onClose: () => void;
}

function AdminMfaChallenge({ onComplete, onClose }: VariantProps): JSX.Element {
  const nextStep = useAuthStore((s) => s.mfaPendingNextStep);
  const availableMethods = useAuthStore((s) => s.mfaPendingAvailableMethods);

  const verifyMutation = useAdminMfaVerify();
  const enrollMutation = useAdminMfaEnroll();
  const enrollConfirmMutation = useAdminMfaEnrollConfirm();
  const webauthnBeginMutation = useWebauthnVerifyBegin();
  const webauthnFinishMutation = useWebauthnVerifyFinish();

  const hasTotp =
    !availableMethods ||
    availableMethods.length === 0 ||
    availableMethods.includes("totp");
  const hasWebauthn = availableMethods?.includes("webauthn") ?? false;
  const webauthnOnly = hasWebauthn && !hasTotp;
  const both = hasWebauthn && hasTotp;

  const [activeMethod, setActiveMethod] = useState<"totp" | "webauthn">(() => {
    if (webauthnOnly && webAuthnSupported()) return "webauthn";
    if (both && webAuthnSupported()) return "webauthn";
    return "totp";
  });

  // ── Enroll branch ────────────────────────────────────────────────────────
  if (nextStep === "enroll") {
    return (
      <ModalShell
        title="Set up two-factor auth"
        subtitle="Add a one-time code from your authenticator app to your sign-in."
        onClose={onClose}
        wide
      >
        <MfaEnrollFlow
          enrollMutation={enrollMutation}
          confirmMutation={enrollConfirmMutation}
          continueLabel="Continue to admin"
          onComplete={onComplete}
          onExpired={onClose}
        />
      </ModalShell>
    );
  }

  // ── Verify branch ────────────────────────────────────────────────────────
  const title =
    activeMethod === "webauthn" ? "Use your passkey" : "Two-factor verification";
  const subtitle =
    activeMethod === "webauthn"
      ? "Verify with your registered passkey to continue."
      : "Enter the 6-digit code from your authenticator app.";

  return (
    <ModalShell title={title} subtitle={subtitle} onClose={onClose}>
      {activeMethod === "webauthn" ? (
        <PasskeyVerifyFlow
          beginMutation={webauthnBeginMutation}
          finishMutation={webauthnFinishMutation}
          onComplete={onComplete}
          onExpired={onClose}
          fallbackSlot={
            both || !hasWebauthn ? (
              <button
                type="button"
                className="text-sm text-text-secondary underline-offset-2 hover:underline dark:text-brand-100/70"
                onClick={() => setActiveMethod("totp")}
              >
                Use authenticator code instead
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
          <MfaVerifyFlow
            verifyMutation={verifyMutation}
            onComplete={onComplete}
            onExpired={onClose}
          />
          {(hasWebauthn || !availableMethods || availableMethods.length === 0) &&
          webAuthnSupported() ? (
            <button
              type="button"
              className="mt-1 text-sm text-text-secondary underline-offset-2 hover:underline dark:text-brand-100/70"
              onClick={() => setActiveMethod("webauthn")}
            >
              Use a passkey instead
            </button>
          ) : null}
        </>
      )}
    </ModalShell>
  );
}

// ── Tenant (app host) variant ─────────────────────────────────────────────────

function TenantMfaChallenge({
  tenantSlug,
  onComplete,
  onClose,
}: VariantProps & { tenantSlug: string }): JSX.Element {
  const nextStep = useAuthStore((s) => s.mfaPendingNextStep);
  const method = useAuthStore((s) => s.mfaPendingMethod);
  const codeEmailSent = useAuthStore((s) => s.mfaPendingCodeEmailSent);

  const totpVerifyMutation = useTenantMfaVerify(tenantSlug);
  const emailVerifyMutation = useTenantMfaEmailVerify(tenantSlug);
  const emailResendMutation = useTenantMfaEmailResend(tenantSlug);
  const webauthnBeginMutation = useTenantWebauthnVerifyBegin(tenantSlug);
  const webauthnFinishMutation = useTenantWebauthnVerifyFinish(tenantSlug);
  const enrollMutation = useTenantMfaEnroll(tenantSlug);
  const enrollConfirmMutation = useTenantMfaEnrollConfirm(tenantSlug);

  const isWebauthn = method === "webauthn";
  const [useEmailFallback, setUseEmailFallback] = useState(false);

  // ── Enroll branch ────────────────────────────────────────────────────────
  if (nextStep === "enroll") {
    return (
      <ModalShell
        title="Set up two-factor auth"
        subtitle="Add a one-time code from your authenticator app to your sign-in."
        onClose={onClose}
        wide
      >
        <MfaEnrollFlow
          enrollMutation={enrollMutation}
          confirmMutation={enrollConfirmMutation}
          continueLabel="Continue to dashboard"
          onComplete={onComplete}
          onExpired={onClose}
        />
      </ModalShell>
    );
  }

  // ── Passkey surface (WA-2) ───────────────────────────────────────────────
  const showPasskey = isWebauthn && !useEmailFallback && webAuthnSupported();
  if (showPasskey) {
    return (
      <ModalShell
        title="Use your passkey"
        subtitle="Verify with your registered passkey to continue."
        onClose={onClose}
      >
        <PasskeyVerifyFlow
          beginMutation={webauthnBeginMutation}
          finishMutation={webauthnFinishMutation}
          onComplete={onComplete}
          onExpired={onClose}
          fallbackSlot={
            <button
              type="button"
              className="text-sm text-text-secondary underline-offset-2 hover:underline dark:text-brand-100/70"
              onClick={() => setUseEmailFallback(true)}
            >
              Email me a code instead
            </button>
          }
        />
      </ModalShell>
    );
  }

  // ── Email-code surface (B3 + WA-2 fallback) ──────────────────────────────
  const isEmailCode = method === "email_code" || isWebauthn;
  if (isEmailCode) {
    return (
      <ModalShell
        title="Enter your verification code"
        subtitle="We emailed you a 6-digit code. Enter it to finish signing in."
        onClose={onClose}
      >
        <MfaEmailVerifyFlow
          verifyMutation={emailVerifyMutation}
          resendMutation={emailResendMutation}
          onComplete={onComplete}
          onExpired={onClose}
          codeEmailSent={isWebauthn ? false : codeEmailSent}
        />
      </ModalShell>
    );
  }

  // ── TOTP surface (default) ───────────────────────────────────────────────
  return (
    <ModalShell
      title="Two-factor verification"
      subtitle="Enter the 6-digit code from your authenticator app."
      onClose={onClose}
    >
      <MfaVerifyFlow
        verifyMutation={totpVerifyMutation}
        onComplete={onComplete}
        onExpired={onClose}
      />
    </ModalShell>
  );
}

// ── Public host component ──────────────────────────────────────────────────────

type MfaVerifyModalProps =
  | {
      variant: "admin";
      /** Navigate to the app after the ceremony commits the session. */
      onComplete: () => void;
      /** Dismiss without completing (expiry / user close). No route change. */
      onClose: () => void;
    }
  | {
      variant: "tenant";
      tenantSlug: string;
      onComplete: () => void;
      onClose: () => void;
    };

/**
 * Visibility is derived from ``mfaPendingToken``: the modal renders nothing
 * once the slot is cleared. The owning Login page also gates on its own local
 * ``open`` flag, but this internal guard is the belt-and-suspenders that makes
 * the "token cleared ⇒ modal gone" invariant hold no matter who cleared it
 * (verify success, TTL timer, or a fresh login attempt).
 *
 * When the token clears WITHOUT a completion (e.g. the proactive TTL timer
 * wipes it while the modal is open), we invoke ``onClose`` once so the Login
 * page can drop its local flag and re-show the form. We do NOT navigate.
 */
export function MfaVerifyModal(props: MfaVerifyModalProps): JSX.Element | null {
  const mfaPendingToken = useAuthStore((s) => s.mfaPendingToken);
  const status = useAuthStore((s) => s.status);
  const { onClose } = props;

  // If the pending slot cleared while the modal is open AND we did not become
  // authenticated (the completion path drives its own onComplete navigate),
  // notify the owner so it can close the modal. Guard on status so a
  // verify-success clear (which flips status→authenticated first, per the
  // reordered loginSuccess) does NOT double-fire onClose over onComplete.
  useEffect(() => {
    if (!mfaPendingToken && status !== "authenticated") {
      onClose();
    }
  }, [mfaPendingToken, status, onClose]);

  if (!mfaPendingToken) return null;

  if (props.variant === "admin") {
    return (
      <AdminMfaChallenge
        onComplete={props.onComplete}
        onClose={props.onClose}
      />
    );
  }
  return (
    <TenantMfaChallenge
      tenantSlug={props.tenantSlug}
      onComplete={props.onComplete}
      onClose={props.onClose}
    />
  );
}
